import React, { useEffect, useRef, useState } from 'react';
import { RequestNewVersion } from '../../../classes/RequestNewVersion';
import { Button } from 'react-bootstrap';

export const CONTACTS_LOAD_ERROR_MESSAGE = 'No fue posible cargar los datos de contacto';

const COPY_ERROR_MESSAGE = 'No fue posible copiar el número';

const COPY_FEEDBACK_MS = 3000;

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();

  try {
    if (!document.execCommand('copy')) {
      throw new Error(COPY_ERROR_MESSAGE);
    }
  } finally {
    document.body.removeChild(textArea);
  }
};

const CopyPhoneButton = ({ phone }) => {
  const [status, setStatus] = useState('idle');
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const normalizedPhone = String(phone === null || phone === undefined ? '' : phone).trim();

  const hasPhone = normalizedPhone !== '';

  const displayPhone = hasPhone ? normalizedPhone : 'No registrado';

  const scheduleReset = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setStatus('idle');
    }, COPY_FEEDBACK_MS);
  };

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(normalizedPhone);

      setStatus('copied');
    } catch (error) {
      console.error('Error al copiar:', error);

      setStatus('error');
    }

    scheduleReset();
  };

  const liveMessage =
    status === 'copied'
      ? 'Número copiado al portapapeles'
      : status === 'error'
        ? COPY_ERROR_MESSAGE
        : '';

  return (
    <>
      <span className="sr-only" role="status" aria-live="polite">
        {liveMessage}
      </span>

      {status === 'copied' ? (
        <div
          style={{
            color: '#00C853',
            fontWeight: '600',
            transition: 'all .2s ease',
          }}
        >
          <i className="fas fa-check-circle mr-1" aria-hidden="true"></i>
          Copiado
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>{displayPhone}</span>

          {hasPhone && (
            <Button
              size="sm"
              variant="outline-primary"
              title="Copiar teléfono"
              aria-label={`Copiar teléfono ${normalizedPhone}`}
              onClick={handleCopy}
            >
              <i className="fas fa-copy" aria-hidden="true"></i>
            </Button>
          )}
        </div>
      )}

      {status === 'error' && (
        <div style={{ color: '#f44336', fontSize: '0.75rem' }}>{COPY_ERROR_MESSAGE}</div>
      )}
    </>
  );
};

export const assignedUsersTableColumns = [
  {
    dataField: 'name',
    text: 'Nombre',
    sort: true,
    style: { maxWidth: '250px', minWidth: '180px', textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
  },
  {
    dataField: 'email',
    text: 'Correo electrónico',
    sort: true,
    style: { maxWidth: '250px', minWidth: '180px', textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
  },
  {
    dataField: 'phone',
    text: 'Teléfono',
    sort: true,
    style: { maxWidth: '250px', minWidth: '180px', textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
    formatter: (cell) => <CopyPhoneButton phone={cell} />,
  },
  {
    dataField: 'actions',
    text: '',
    sort: false,
    style: { maxWidth: '50px', minWidth: '50px', textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
  },
];

export const searchUsersTableColumns = [
  {
    dataField: 'fullname',
    text: 'Nombre',
    sort: true,
    style: { maxWidth: '250px', minWidth: '180px', textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
  },
  {
    dataField: 'email',
    text: 'Correo electrónico',
    sort: true,
    style: { maxWidth: '250px', minWidth: '180px', textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
  },
  {
    dataField: 'actions',
    text: '',
    sort: false,
    style: { maxWidth: '50px', minWidth: '50px', textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
  },
];

export const getUsersWithPermissionsToSeeExpedient = async (
  expedientId,
  setCollection,
  loader,
  setLoadError = () => {}
) => {
  loader.show(true);
  setLoadError('');

  const request = new RequestNewVersion(
    `${process.env.REACT_APP_URL_API_LARAVEL}/electronic_expedients/get_user_permissions/${expedientId}`,
    'get',
    null,
    {}
  );

  const { code, response } = await request.executeRequest();

  if (code !== 200) {
    setCollection([]);
    setLoadError(CONTACTS_LOAD_ERROR_MESSAGE);
    loader.show(false);
    return;
  }

  const { expedient, permissions, contactsUnavailable } = response;
  const users = [...(expedient || []), ...(permissions || [])];
  const mappedUsers = users.map((user) => ({ ...user, idreact: user.email.concat(user.id) }));

  setCollection(mappedUsers);
  setLoadError(contactsUnavailable ? CONTACTS_LOAD_ERROR_MESSAGE : '');
  loader.show(false);
};

export const handleSubmitAssignUserSearch = async (e, search, setCollection, loader) => {
  e.preventDefault();
  loader.show(true);

  search = search === '' ? '%20' : search;

  const request = new RequestNewVersion(
    `${process.env.REACT_APP_URL_API_LARAVEL}/electronic_expedients/search_users/${search}`,
    'get',
    null,
    {}
  );

  const { code, response } = await request.executeRequest();

  if (code !== 200) {
    loader.show(false);
    setCollection([]);
    return;
  }

  const { users } = response;
  const usersFound = users || [];
  const mappedUsers = usersFound.map((user) => ({ ...user, idreact: user.email.concat(user.id) }));

  setCollection(mappedUsers);
  loader.show(false);
};

export const addUserToExpedientPermission = async (
  id,
  expedientId,
  setUsersWithPermission,
  loader,
  setLoadError = () => {}
) => {
  loader.show(true);

  const request = new RequestNewVersion(
    `${process.env.REACT_APP_URL_API_LARAVEL}/electronic_expedients/add_users_to_expedient_permissions/${expedientId}`,
    'post',
    null,
    {
      users: [id],
    }
  );

  const { code } = await request.executeRequest();

  if (code !== 200) {
    loader.show(false);
    return;
  }

  getUsersWithPermissionsToSeeExpedient(expedientId, setUsersWithPermission, loader, setLoadError);
  loader.show(false);
};

export const removeUserToExpedientPermission = async (
  id,
  expedientId,
  setUsersWithPermission,
  loader,
  setLoadError = () => {}
) => {
  loader.show(true);

  const request = new RequestNewVersion(
    `${process.env.REACT_APP_URL_API_LARAVEL}/electronic_expedients/remove_users_to_expedient_permissions/${expedientId}`,
    'post',
    null,
    {
      users: [id],
    }
  );

  const { code } = await request.executeRequest();

  if (code !== 200) {
    loader.show(false);
    return;
  }

  getUsersWithPermissionsToSeeExpedient(expedientId, setUsersWithPermission, loader, setLoadError);
  loader.show(false);
};
